import { useEffect, useState } from 'react';
import {
  ButtonPseudo,
  IconClose,
  IconDown,
  IconSearch,
  IconUp,
  InputSize,
  InputText,
  Checkbox,
  ButtonPrimary,
  ButtonSize,
  ClickOutsideDetector,
  IconFilter,
  IconRefresh,
  SidePanel,
  SidePanelType,
} from '@ui/src';
import { useAppDispatch } from '@store';
import {
  onFilterChange,
  onChangeOnlyArchived,
} from '../../../hoc/user-list/store/slices/users';
import { ColumnHeadTypes } from './types';

import cn from './column.module.css';

const ColumnHead = ({
  title,
  className,
  placeholder,
  description,
  value,
  onSearch,
  roles,
  selectedRoles: fileteredSelectedRoles,
  onlyArchived,
  userColumn,
  isEqual,
  actionColumn,
  columnData,
  setResetPopupVisible,
}: ColumnHeadTypes) => {
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [inputOpen, setInputOpen] = useState(Boolean(value));
  const [onlyArchivedValue, setOnlyArchivedValue] = useState(false);

  const dispatch = useAppDispatch();

  let content: React.JSX.Element;

  useEffect(() => {
    setOnlyArchivedValue(onlyArchived as boolean);
  }, [onlyArchived]);

  if (onSearch && placeholder) {
    content = inputOpen ? (
      <div className={cn.columnHeaderWrapper}>
        <InputText
          placeholder={placeholder}
          name="search"
          size={InputSize.SMALL}
          labelClassName={cn.searchInput}
          value={value}
          onChange={(e) => onSearch(e.target.value)}
        />
        <ButtonPseudo
          icon={<IconClose />}
          className="button-white"
          onClick={() => {
            setInputOpen(false);
            onSearch('');
          }}
        />
      </div>
    ) : (
      <div className={cn.columnHeaderWrapper}>
        <div className={cn.columnHeaderWrapperLeft}>
          <div className={cn.columnTitle}>{title}</div>
          {actionColumn && !isEqual && columnData && setResetPopupVisible && (
            <ButtonPseudo
              className={`button-white ${cn.searchButton}`}
              icon={<IconRefresh />}
              onClick={() => setResetPopupVisible(true)}
            />
          )}
          <ButtonPseudo
            icon={<IconSearch />}
            className={`button-white ${cn.searchButton}`}
            onClick={() => setInputOpen(true)}
          />
        </div>
        {userColumn && (
          <div className={cn.columnHeaderWrapperRight}>
            <div className={cn.roleMenuWrapper}>
              <ClickOutsideDetector setOptionsListVisible={setRoleMenuOpen}>
                <>
                  <button
                    type="button"
                    className={`${cn.roleMenuButton} ${
                      fileteredSelectedRoles?.length ? cn.active : ''
                    }`}
                    onClick={() => setRoleMenuOpen(!roleMenuOpen)}
                  >
                    <div className={cn.textWrapper}>Role</div>
                    <div className={cn.iconWrapper}>
                      {roleMenuOpen ? <IconUp /> : <IconDown />}
                    </div>
                  </button>
                  {roleMenuOpen && (
                    <div className={cn.roleMenu}>
                      {roles?.map((item) => (
                        <div className={cn.roleItemWrapper} key={item.value}>
                          <Checkbox
                            type="checkbox"
                            name={`role-${item.value}`}
                            title={item.name}
                            checked={selectedRoles?.includes(
                              item.value as string,
                            )}
                            onChange={() => {
                              let nextSelectedRoles = ([] as string[]).concat(
                                selectedRoles,
                              );

                              if (
                                nextSelectedRoles?.includes(
                                  item.value as string,
                                )
                              ) {
                                nextSelectedRoles = nextSelectedRoles.filter(
                                  (role) => role !== item.value,
                                );
                              } else {
                                nextSelectedRoles.push(item.value as string);
                              }

                              setSelectedRoles(nextSelectedRoles);
                            }}
                          />
                        </div>
                      ))}
                      <div className={cn.roleMenuFooter}>
                        <ButtonPrimary
                          size={ButtonSize.SMALL}
                          text="Apply"
                          className={cn.applyButton}
                          onClick={() => {
                            dispatch(onFilterChange(selectedRoles));
                            setRoleMenuOpen(false);
                          }}
                        />
                        <ButtonPseudo
                          size={ButtonSize.SMALL}
                          text="Clear"
                          disabled={!selectedRoles?.length}
                          onClick={() => {
                            setSelectedRoles([]);
                            dispatch(onFilterChange([]));
                            setRoleMenuOpen(false);
                          }}
                        />
                      </div>
                    </div>
                  )}
                </>
              </ClickOutsideDetector>
            </div>
            <div className={cn.filtersWrapper}>
              <button
                type="button"
                className={`${cn.filterButton} ${
                  onlyArchived ? cn.active : ''
                }`}
                aria-label="Filter button"
                onClick={() => setFilterMenuOpen(!filterMenuOpen)}
              >
                <IconFilter />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  } else {
    content = (
      <>
        <div className={cn.columnHeaderWrapper}>
          <div className={cn.columnHeaderWrapperLeft}>
            <div className={cn.columnTitle}>{title}</div>
            {actionColumn && !isEqual && columnData && setResetPopupVisible && (
              <ButtonPseudo
                className={`button-white ${cn.searchButton}`}
                icon={<IconRefresh />}
                onClick={() => setResetPopupVisible(true)}
              />
            )}
          </div>
        </div>
        {description && (
          <div className={cn.columnDescription}>{description}</div>
        )}
      </>
    );
  }
  return (
    <div className={`${cn.columnHeader} ${className}`}>
      {content}
      <SidePanel
        type={SidePanelType.Accounts}
        closeOnClickOutside
        isOpen={filterMenuOpen}
        onClose={() => setFilterMenuOpen(false)}
        title="Filters"
        sidepanelExpandedKey="employee-filters"
        footer={
          <>
            <ButtonPrimary
              text="Apply filter"
              onClick={() => {
                dispatch(onFilterChange(selectedRoles));
                dispatch(onChangeOnlyArchived(onlyArchivedValue));
                setFilterMenuOpen(false);
              }}
              bold
            />
            <ButtonPseudo
              text="Close"
              onClick={() => {
                setFilterMenuOpen(false);
              }}
              bold
            />
          </>
        }
      >
        <>
          <div className={cn.filterRow}>
            <Checkbox
              type="checkbox"
              name="onlyArchived"
              title="Show only Archived employee"
              checked={onlyArchivedValue}
              onChange={(e) => setOnlyArchivedValue(e.target.checked)}
            />
          </div>
          <div className={cn.filterTitle}>Employee Role</div>
          {roles?.map((item) => (
            <div className={cn.filterRoleRow} key={item.value}>
              <Checkbox
                type="checkbox"
                name={`role-${item.value}`}
                title={item.name}
                checked={selectedRoles?.includes(item.value as string)}
                onChange={() => {
                  let nextSelectedRoles = ([] as string[]).concat(
                    selectedRoles,
                  );

                  if (nextSelectedRoles?.includes(item.value as string)) {
                    nextSelectedRoles = nextSelectedRoles.filter(
                      (role) => role !== item.value,
                    );
                  } else {
                    nextSelectedRoles.push(item.value as string);
                  }

                  setSelectedRoles(nextSelectedRoles);
                }}
              />
            </div>
          ))}
        </>
      </SidePanel>
    </div>
  );
};

export default ColumnHead;
